<script setup>
defineProps({
  title: {
    type: String,
    default: () => '',
  },
  logo: {
    type: String,
    default: () => '',
  },
  url: {
    type: String,
    default: () => '',
  },
  view: {
    type: String,
    default: () => '',
  },
  backgroundColor: {
    type: String,
    default: () => '',
  },
  isFirst: {
    type: String,
    default: () => '',
  },
});
defineEmits(['mouseenter', 'mouseleave']);
</script>
<template>
  <CTagDetect
    v-if="url && url.length > 0"
    :to="url"
    :class="[
      `dealer dealer--with-url dealer--${backgroundColor} dealer--${view}`,
      isFirst && view === 'brand-strategic' ? 'dealer--first' : '',
    ]"
    @mouseenter="
      (view !== 'nav' || view !== 'nav-mobile') && $emit('mouseenter')
    "
    @mouseleave="
      (view !== 'nav' || view !== 'nav-mobile') && $emit('mouseleave')
    "
  >
    <div class="dealer__top">
      <NuxtImg
        v-if="logo?.src"
        :src="logo?.src"
        :alt="logo?.alt"
        :height="logo?.height"
        :width="logo?.width"
        class="dealer__logo"
        loading="lazy"
      />
    </div>
    <span v-if="title" class="dealer__title dealer__title--arrow">
      {{ title }}
      <CIcon
        :name="
          view === 'nav-mobile'
            ? 'direction-small-white'
            : 'direction-small-black'
        "
        width="16"
        height="16"
        class="dealer__icon"
      />
    </span>
  </CTagDetect>
  <div
    v-else
    :class="[
      `dealer dealer--${view} dealer--${backgroundColor}`,
      isFirst && view === 'brand-strategic' ? 'dealer--first' : '',
    ]"
  >
    <div class="dealer__top">
      <NuxtImg
        v-if="logo?.src"
        :src="logo?.src"
        :alt="logo?.alt"
        :height="logo?.height"
        :width="logo?.width"
        class="dealer__logo"
        loading="lazy"
      />
    </div>
    <template v-if="view === 'brand-strategic'">
      <div class="dealer__wrap">
        <template v-if="isFirst">
          <span>{{ $t('partners.strategic_partner') }}</span>
        </template>
        <span v-if="title" class="dealer__title" v-html="title"></span>
      </div>
    </template>
    <template v-else>
      <span v-if="title" class="dealer__title" v-html="title"></span>
    </template>
  </div>
</template>
<style scoped lang="scss">
.dealer {
  $parent: &;
  border-radius: 4px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  justify-content: center;
  justify-items: center;
  height: 100%;
  min-height: 104px;
  padding: 8px;
  text-decoration: unset;
  text-align: center;
  @include media-breakpoint-up(sm) {
    border-radius: 8px;
    gap: 20px;
    min-height: 176px;
    padding: 32px;
  }
  @include media-breakpoint-up(md) {
    gap: 34px;
    min-height: 190px;
    &--with-url {
      &:hover {
        #{$parent} {
          &__title {
            color: $primary;
          }
          &__icon {
            &:deep(path) {
              stroke: $primary;
            }
          }
        }
      }
    }
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(border-radius, 8);
    @include responsive-spacing(gap, 34);
    @include responsive-spacing(min-height, 190);
    @include responsive-spacing(padding, 32);
  }
  &--white {
    background-color: $gray;
  }
  &--black {
    background-color: rgba($surface-container-opacity, 0.5);
    #{$parent} {
      &__title {
        color: $white;
      }
      &__icon {
        &:deep(path) {
          stroke: $white;
        }
      }
    }
  }
  &--gray {
    background-color: $white;
  }
  &__title {
    transition: all 0.3s linear;
    color: $black;
    @include body-3-regular;
    &--arrow {
      align-items: center;
      display: grid;
      grid-template-columns: auto 16px;
      gap: 10px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 10);
        grid-template-columns: auto px-to-vw(16);
      }
    }
  }
  &__top {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
  &__icon {
    height: auto;
    width: 100%;
    &:deep(path) {
      transition: all 0.3s linear;
    }
  }
  &__logo {
    height: auto;
    width: 100%;
  }
  &__wrap {
    display: flex;
    flex-direction: column;
    gap: 8px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
  }
  &--default {
    #{$parent} {
      &__logo {
        width: 58px;
        @include media-breakpoint-up(sm) {
          width: 72px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(width, 72);
        }
      }
    }
  }
  &--nav {
    align-items: center;
    background-color: transparent;
    border-radius: 0px;
    gap: 12px;
    grid-template-columns: 32px auto;
    grid-template-rows: auto;
    justify-content: start;
    min-height: 32px;
    padding: 0;
    margin-top: 8px;
    &:first-child,
    &:nth-child(2) {
      margin-top: 0;
    }
    @include media-breakpoint-up(sm) {
      min-height: 40px;
    }
    @include media-breakpoint-up(md) {
      margin-top: 32px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 12);
      @include responsive-spacing(margin-top, 32);
      @include responsive-spacing(min-height, 40);
      grid-template-columns: px-to-vw(32) auto;
      &:first-child,
      &:nth-child(2) {
        margin-top: 0;
      }
    }
    #{$parent} {
      gap: 8px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
      }
      &__logo {
        height: 32px;
        padding: 2px;
        width: 32px;
        @include media-breakpoint-up(md) {
          height: 40px;
          padding: 4px;
          width: 40px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(height, 40);
          @include responsive-spacing(padding, 4);
          @include responsive-spacing(width, 40);
        }
      }
      &__title {
        @include link-small;
        &--arrow {
          gap: 8px;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(gap, 8);
          }
        }
      }
    }
    @include media-breakpoint-up(sm) {
      grid-template-columns: 40px auto;
    }
    @include media-breakpoint-up(xl) {
      grid-template-columns: px-to-vw(40) auto;
    }
  }
  &--nav-mobile {
    align-items: center;
    background-color: transparent;
    border-radius: 0px;
    gap: 12px;
    grid-template-columns: 32px auto;
    grid-template-rows: auto;
    justify-content: start;
    min-height: 32px;
    padding: 0;
    margin-top: 8px;
    &:first-child,
    &:nth-child(2) {
      margin-top: 0;
    }
    @include media-breakpoint-up(sm) {
      grid-template-columns: 40px auto;
      min-height: 40px;
    }
    @include media-breakpoint-up(md) {
      margin-top: 32px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 12);
      @include responsive-spacing(margin-top, 32);
      @include responsive-spacing(min-height, 40);
      grid-template-columns: px-to-vw(32) auto;
      &:first-child,
      &:nth-child(2) {
        margin-top: 0;
      }
    }
    #{$parent} {
      gap: 8px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
      }
      &__logo {
        background-color: $white;
        border-radius: 4px;
        height: 32px;
        padding: 2px;
        width: 32px;
        @include media-breakpoint-up(sm) {
          height: 40px;
          padding: 4px;
          width: 40px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-radius, 4);
          @include responsive-spacing(height, 40);
          @include responsive-spacing(padding, 4);
          @include responsive-spacing(width, 40);
        }
      }
      &__title {
        @include link-small;
        color: $white;
        &--arrow {
          gap: 8px;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(gap, 8);
          }
        }
      }
    }
    @include media-breakpoint-up(sm) {
      grid-template-columns: 40px auto;
    }
    @include media-breakpoint-up(xl) {
      grid-template-columns: px-to-vw(40) auto;
    }
  }
  &--brand {
    align-content: center;
    grid-template-rows: auto;
    #{$parent} {
      &__logo {
        width: 58px;
        @include media-breakpoint-up(sm) {
          width: 72px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(width, 72);
        }
      }
    }
    @include media-breakpoint-up(sm) {
      grid-template-rows: auto;
    }
    @include media-breakpoint-up(md) {
      grid-template-rows: auto;
    }
    @include media-breakpoint-up(xl) {
      grid-template-rows: auto;
    }
  }
  &--brand-strategic {
    background-color: transparent;
    border-radius: unset;
    gap: 8px;
    grid-template-rows: 160px auto;
    justify-items: center;
    min-height: unset;
    padding: 0;
    #{$parent} {
      &__top {
        border-radius: 8px;
        padding: 12px;
        width: 100%;
      }
      &__logo {
        height: auto;
        max-width: 136px;
        width: 100%;
      }
      &__title {
        padding: 0 8px;
      }
    }
    @include media-breakpoint-up(sm) {
      grid-template-rows: 176px auto;
      #{$parent} {
        &__top {
          padding: 20px;
        }
      }
    }
    @include media-breakpoint-up(md) {
      grid-template-rows: 190px auto;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
      grid-template-rows: px-to-vw(190) auto;
      #{$parent} {
        &__top {
          @include responsive-spacing(border-radius, 8);
          @include responsive-spacing(padding, 20);
        }
        &__logo {
          @include responsive-spacing(max-width, 136);
        }
        &__title {
          @include responsive-spacing(padding-left, 8);
          @include responsive-spacing(padding-right, 8);
        }
      }
    }

    &.dealer--first {
      gap: 16px;
      grid-template-rows: 328px auto;
      margin-bottom: 32px;
      #{$parent} {
        &__top {
          padding: 64px;
        }
        &__logo {
          max-width: 200px;
        }
        &__wrap {
          @include body-1-regular;
        }
        &__title {
          @include h5;
        }
      }
      @include media-breakpoint-up(sm) {
        gap: 24px;
        grid-template-rows: 389px auto;
        margin-bottom: 56px;
        #{$parent} {
          &__top {
            max-width: 389px;
          }
        }
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 24);
        @include responsive-spacing(margin-bottom, 56);
        grid-template-rows: px-to-vw(389) auto;
        #{$parent} {
          &__top {
            @include responsive-spacing(padding, 64);
            @include responsive-spacing(max-width, 389);
          }
          &__logo {
            @include responsive-spacing(max-width, 200);
          }
        }
      }
    }
    &.dealer--white {
      #{$parent} {
        &__top {
          background-color: $gray;
        }
      }
    }
    &.dealer--gray {
      #{$parent} {
        &__top {
          background-color: $white;
        }
      }
    }
    &.dealer--black {
      #{$parent} {
        &__top {
          background-color: rgba($surface-container-opacity, 0.5);
        }
      }
    }
  }
}
</style>
